import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import { memo, useEffect, useState } from 'react';
import SimpleSearchInput from 'components/SearchInputNavbar';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useMutation, useQueries, useQuery } from 'react-query';
import IncomeMoneyFilterDrawer from '../../../components/dashboard/IncomeMoneyFilterDrawer';
import { useLocation } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';

const Money = ({ accounts }) => {
  const { search } = useLocation();
  const [{ user }] = useAuth();

  const axiosPrivate = useAxiosPrivate();

  const [openModal, setOpenModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedTotals, setSelectedTotals] = useState({ uzs: 0, usd: 0 });
  const [selectedAccountId, setSelectedAccountId] = useState('');

  const { isLoading, data, refetch } = useQuery({
    queryKey: ['/dictionary/unaccpetedpayments', search],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/dictionary/unaccpetedpayments${search || ''}`
      );
      return response.data?.data;
    },
  });

  const getPayments = useMutation({
    mutationFn: () => axiosPrivate.get('admin/accounts/getpayments'),

    onSuccess: (fetch) => {
      if (fetch.status) {
        refetch();
      }
    },
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(data?.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const isAllSelected = data?.length > 0 && selectedIds.length === data?.length;

  useEffect(() => {
    const selectedItems = data?.filter((row) => selectedIds?.includes(row.id));
    const totals = selectedItems?.reduce(
      (acc, curr) => {
        acc.uzs += parseFloat(curr.sum || 0);
        acc.usd += parseFloat(curr.valute || 0);
        return acc;
      },
      { uzs: 0, usd: 0 }
    );
    setSelectedTotals(totals);
  }, [selectedIds, data]);

  const handleAcceptPayments = async () => {
    if (!selectedAccountId) {
      alert('Iltimos, kassani tanlang!');
      return;
    }

    try {
      await axiosPrivate.post('/admin/payments/accepts', {
        ids: selectedIds,
        account_id: selectedAccountId,
      });
      setSelectedIds([]);
      setSelectedAccountId('');
      refetch();
    } catch (error) {
      console.error('Qabul qilishda xatolik:', error);
    }
  };

  return (
    <div className='component-list-wrapper'>
      <div className='component-list-header mb-2'>
        <div className='header-actions-container py-3 flex flex-row items-center'>
          <div className='header-actions filter-box flex items-center px-4 w-full'>
            <Button
              variant='base'
              color='primary'
              startIcon={<i className='bi bi-filter' />}
              className='!mr-2'
              onClick={() => setOpenFilter(true)}
            >
              Filter
            </Button>
            <SimpleSearchInput inputKey='name' />
          </div>
          <div className='header-actions action-buttons-box py-3 px-4 flex gap-2 items-center justify-center'>
            <Button
              variant='base'
              color='info'
              onClick={getPayments.mutate}
              disabled={getPayments.isLoading}
              className='whitespace-nowrap'
              endIcon={
                <i
                  className={`bi bi-arrow-repeat${
                    getPayments.isLoading ? ' animate-spin' : ''
                  }`}
                />
              }
            >
              To'lovlarni yuklash
            </Button>
            <Button
              variant='base'
              color='info'
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <i
                className={`bi bi-arrow-repeat${
                  isLoading ? ' animate-spin' : ''
                }`}
              />
            </Button>
            <Button
              variant='base'
              color='success'
              onClick={() => setOpenModal(true)}
            >
              <i className='bi bi-plus-circle' />
            </Button>
            <Button
              variant='base'
              color='default'
              onClick={() => setOpen(true)}
            >
              <i className='bi bi-gear' />
            </Button>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className='px-4 py-2 font-semibold bg-gray-100 border-t flex items-center justify-between'>
            <span className='mr-4'>
              {selectedIds.length} ta toʻlov tanlandi. Jami:{' '}
              {selectedTotals.uzs.toLocaleString()} UZS,{' '}
              {selectedTotals.usd.toLocaleString()} USD
            </span>
            <div className='mr-4 min-w-72 flex-grow-0'>
              <FormControl fullWidth size='small'>
                <InputLabel>{'Kassa'}</InputLabel>
                <Select
                  value={selectedAccountId}
                  label={'Kassa'}
                  fullWidth
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                >
                  <MenuItem value=''>
                    <em>{'Tanlang'}</em>
                  </MenuItem>
                  {accounts.map((acc) => (
                    <MenuItem key={acc.id} value={acc.id}>
                      {acc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <Button
              variant='base'
              color='success'
              size='small'
              onClick={handleAcceptPayments}
              disabled={!selectedAccountId}
            >
              Qabul qilish
            </Button>
          </div>
        )}
      </div>

      <div className='component-table-wrapper h-[calc(100vh-180px)]'>
        <TableContainer className='tab-table-container' component='div'>
          <Table stickyHeader component='div'>
            <TableHead component='div'>
              <TableRow component='div'>
                <TableCell component='div' padding='checkbox'>
                  <Checkbox
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < data?.length
                    }
                  />
                </TableCell>
                <TableCell component='div'>Sana</TableCell>
                <TableCell component='div'>To'lov id</TableCell>
                <TableCell component='div'>UZS | USD</TableCell>
                <TableCell component='div'>Izoh</TableCell>
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableBody component='div'>
                <TableRow>
                  <TableCell colSpan={'5'}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody component='div'>
                {data?.length > 0 ? (
                  data?.map((row) => (
                    <TableRow key={row?.id} component='div' hover>
                      <TableCell component='div' padding='checkbox'>
                        <Checkbox
                          checked={selectedIds?.includes(row?.id)}
                          onChange={() => handleSelectOne(row?.id)}
                        />
                      </TableCell>
                      <TableCell component='div'>
                        {moment(row?.date)?.format('DD/MM/YYYY')}
                      </TableCell>
                      <TableCell component='div'>{row?.ext_id}</TableCell>
                      <TableCell component='div'>
                        {row?.sum} | {row?.valute}
                      </TableCell>
                      <TableCell component='div'>{row?.comment}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow component='div'>
                    <TableCell align='center' colSpan={'5'}>
                      Maʼlumot topilmadi
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </div>

      {openModal && null}

      <IncomeMoneyFilterDrawer open={openFilter} setOpen={setOpenFilter} />
    </div>
  );
};

export default memo(Money);
